import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const SESSION_PATH = '/gateway/operator/login/session';

/**
 * Module-level promise cache: prevents duplicate session calls during
 * React StrictMode double-mounting in development. The first mount creates
 * and caches the session promise; the remount reuses it.
 */
const pendingSessions = new Map();

/**
 * Module-level set of attemptIds for which the session call has already
 * completed successfully. Guards against re-firing the POST if the effect
 * is re-run after the in-flight cache has been cleared (e.g., hot reload,
 * parent remount with the same attemptId, or a future caller that passes
 * a non-memoised sessionData reference).
 */
const completedSessions = new Set();

/**
 * Creates a login session by forwarding token data to the operator gateway.
 *
 * @param {string|null} gatewayBaseUrl – operator connector gateway base URL
 * @param {Object|null} sessionData – { access_token, refresh_token, ttl, attemptId, userIdentifier }
 * @returns {{ status: 'idle'|'loading'|'success'|'error', error: string|null }}
 */
export default function useLoginSession(gatewayBaseUrl, sessionData) {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!sessionData?.attemptId) {
      setStatus('idle');
      return;
    }

    if (!gatewayBaseUrl) {
      setStatus('error');
      setError('Gateway base URL is required');
      return;
    }

    try {
      new URL(`${gatewayBaseUrl}${SESSION_PATH}`);
    } catch {
      setStatus('error');
      setError('Invalid gateway URL');
      return;
    }

    const { access_token, refresh_token, ttl, attemptId, userIdentifier } = sessionData;
    const sessionKey = `${attemptId}`;

    // Idempotency guard: if this attemptId already completed successfully,
    // never re-fire the POST regardless of effect re-runs.
    if (completedSessions.has(sessionKey)) {
      setStatus('success');
      setError(null);
      return;
    }

    const applyResult = (result) => {
      if (!mountedRef.current) return;
      if (result.cancelled) return;
      if (result.error) {
        setStatus('error');
        setError(result.error);
      } else {
        setStatus('success');
        setError(null);
      }
    };

    // Check if a session promise already exists (StrictMode remount case)
    if (!pendingSessions.has(sessionKey)) {
      setStatus('loading');
      setError(null);

      const effectiveAttemptId = attemptId || sessionStorage.getItem('attemptId');

      const promise = axios
        .post(
          `${gatewayBaseUrl}${SESSION_PATH}`,
          { access_token, refresh_token, ttl, attemptId: effectiveAttemptId, userIdentifier },
          {
            headers: {
              'Content-Type': 'application/json',
              'X-Attempt-Id': effectiveAttemptId,
              'X-Correlation-Id': crypto.randomUUID(),
            },
          },
        )
        .then(() => {
          completedSessions.add(sessionKey);
          return { success: true };
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            return { cancelled: true };
          }
          return {
            error: err.response?.data?.error || err.message || 'Login session call failed',
          };
        })
        .finally(() => {
          // Clear the in-flight entry once settled so memory does not grow,
          // but only after completedSessions has been updated on success.
          pendingSessions.delete(sessionKey);
        });

      pendingSessions.set(sessionKey, promise);
      promise.then(applyResult);
    } else {
      // If promise already exists (remount case), wait for it and update status
      pendingSessions.get(sessionKey).then(applyResult);
    }

    return () => {
      // Don't abort on unmount - let the promise complete for other consumers.
      // The mountedRef guard above prevents setState on an unmounted instance.
    };
  }, [gatewayBaseUrl, sessionData]);

  return { status, error };
}
